import React, { useCallback, useEffect, useState } from 'react';
import { DatePicker, Select, Spin, Space, Table, Tooltip } from 'antd';
import dayjs from 'dayjs';
import pmApi from './pmApi';

/**
 * Event-sourced revenue & MLR beside the legacy parity snapshot. Every naira
 * traces to an append-only pricing event; the legacy figure is displayed
 * only to be compared against (it is the number this ledger retires).
 */

const fmtMoney = (v) => `₦${Number(v || 0).toLocaleString('en-NG', { maximumFractionDigits: 0 })}`;

export default function AnalyticsTab({ meta }) {
  const [range, setRange] = useState([dayjs().startOf('year'), dayjs()]);
  const [group, setGroup] = useState('month');
  const [revenue, setRevenue] = useState(null);
  const [mlr, setMlr] = useState(null);
  const [encounters, setEncounters] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') };
    const [r, m, e] = await Promise.all([
      pmApi.revenue({ ...params, group }),
      pmApi.mlr(params),
      pmApi.encounterAnalytics(params),
    ]);
    if (r.ok) setRevenue(r.data);
    if (m.ok) setMlr(m.data);
    if (e.ok) setEncounters(e.data);
    setLoading(false);
  }, [range, group]);

  useEffect(() => { load(); }, [load]);

  const groupLabel = (grp) => {
    if (group === 'plan') {
      const plan = (meta?.plans || []).find((p) => Number(p.plan_id) === Number(grp));
      return plan?.plan_name || `plan ${grp}`;
    }
    return grp === null || grp === undefined ? '—' : String(grp);
  };

  const rows = revenue?.rows || [];
  const maxTotal = Math.max(1, ...rows.map((r) => Math.abs(Number(r.total))));

  const columns = [
    { title: group, dataIndex: 'grp', key: 'grp', render: groupLabel },
    {
      title: 'Revenue (live vs backfill)',
      key: 'bar',
      render: (_, r) => {
        const total = Number(r.total) || 0;
        const live = Number(r.live_total) || 0;
        const backfill = Number(r.backfill_total) || 0;
        const width = Math.max(2, Math.round((Math.abs(total) / maxTotal) * 100));
        const livePct = total !== 0 ? (live / total) * 100 : 0;
        return (
          <Tooltip title={`live ${fmtMoney(live)} · backfill ${fmtMoney(backfill)}`}>
            <div style={{ width: `${width}%`, minWidth: 40 }}>
              <div className="pm-split-bar">
                <i className="pm-live" style={{ width: `${livePct}%` }} />
                <i className="pm-backfill" style={{ width: `${100 - livePct}%` }} />
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: (v) => <b style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(v)}</b>,
    },
    { title: 'Events', dataIndex: 'events', key: 'events', align: 'right' },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 14 }} wrap>
        <DatePicker.RangePicker
          value={range}
          onChange={(v) => v && setRange(v)}
          allowClear={false}
        />
        <Select
          value={group}
          onChange={setGroup}
          style={{ width: 140 }}
          options={[
            { value: 'month', label: 'By month' },
            { value: 'plan', label: 'By plan' },
            { value: 'band', label: 'By band' },
            { value: 'event_type', label: 'By event type' },
          ]}
        />
      </Space>

      {loading && !revenue ? (
        <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>
      ) : (
        <>
          <div className="pm-tiles" style={{ marginBottom: 14 }}>
            <div className="pm-tile">
              <div className="pm-tile-label">Revenue (events)</div>
              <div className="pm-tile-value">{fmtMoney(revenue?.total)}</div>
              <div className="pm-tile-sub">{revenue?.events ?? 0} events · {revenue?.basis}</div>
            </div>
            <div className="pm-tile">
              <div className="pm-tile-label">Claims cost</div>
              <div className="pm-tile-value">{fmtMoney(mlr?.claims_cost)}</div>
              <div className="pm-tile-sub">consumption amount — same expression as dashboards</div>
            </div>
            <div className="pm-tile">
              <div className="pm-tile-label">MLR</div>
              <div className="pm-tile-value">{mlr?.mlr !== null && mlr?.mlr !== undefined ? `${(mlr.mlr * 100).toFixed(1)}%` : '—'}</div>
              <div className="pm-tile-sub">claims ÷ premium, both from records</div>
            </div>
            <div className="pm-tile" style={{ borderStyle: 'dashed' }}>
              <div className="pm-tile-label">Legacy snapshot (parity only)</div>
              <div className="pm-tile-value" style={{ color: 'var(--pm-ink-faint)' }}>
                {fmtMoney(revenue?.legacy_snapshot?.active_policy_value)}
              </div>
              <div className="pm-tile-sub">{revenue?.legacy_snapshot?.caveat}</div>
            </div>
          </div>

          <div className="pm-legend" style={{ marginBottom: 6 }}>
            <span><i style={{ background: 'var(--pm-accent)' }} />live events</span>
            <span><i style={{ background: 'var(--pm-blue)' }} />backfill (segment finance can exclude)</span>
            {Number(revenue?.reversals) !== 0 ? <span>reversals: {fmtMoney(revenue?.reversals)}</span> : null}
          </div>

          <Table
            size="small"
            rowKey={(r) => String(r.grp)}
            columns={columns}
            dataSource={rows}
            pagination={false}
          />

          {encounters ? (
            <>
              <h4 style={{ margin: '22px 0 8px', fontSize: 13, fontWeight: 700, color: 'var(--pm-accent-ink)' }}>
                Encounters &amp; mapping
              </h4>
              <div className="pm-tiles" style={{ marginBottom: 14 }}>
                <div className="pm-tile">
                  <div className="pm-tile-label">Encounters in period</div>
                  <div className="pm-tile-value">
                    {(encounters.by_checkin_type || []).reduce((a, r) => a + Number(r.encounters), 0).toLocaleString()}
                  </div>
                  <div className="pm-tile-sub">every check-in type, data-driven — new types appear automatically</div>
                </div>
                <div className="pm-tile" style={{ borderColor: encounters.unmapped_total > 0 ? 'var(--pm-warn)' : undefined }}>
                  <div className="pm-tile-label">Unmapped items</div>
                  <div className="pm-tile-value" style={{ color: encounters.unmapped_total > 0 ? 'var(--pm-warn)' : undefined }}>
                    {Number(encounters.unmapped_total).toLocaleString()}
                  </div>
                  <div className="pm-tile-sub">{encounters.unmapped_definition}</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) minmax(300px, 2fr)', gap: 14, alignItems: 'start' }}>
                <Table
                  size="small"
                  rowKey={(r) => r.checkin_type}
                  columns={[
                    { title: 'Check-in type', dataIndex: 'checkin_type', key: 't' },
                    { title: 'Encounters', dataIndex: 'encounters', key: 'n', align: 'right' },
                  ]}
                  dataSource={encounters.by_checkin_type || []}
                  pagination={false}
                />
                <Table
                  size="small"
                  rowKey={(r) => `${r.benefit_item}:${r.month}`}
                  columns={[
                    { title: 'Benefit item (mapping)', dataIndex: 'benefit_item', key: 'b' },
                    { title: 'Month', dataIndex: 'month', key: 'm' },
                    { title: 'Cases', dataIndex: 'cases', key: 'c', align: 'right' },
                  ]}
                  dataSource={encounters.by_benefit_item || []}
                  pagination={(encounters.by_benefit_item || []).length > 12 ? { pageSize: 12, size: 'small' } : false}
                  locale={{ emptyText: 'No mapped lines in this period yet — mapping fills this in' }}
                />
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
